import type { IntlShape } from "react-intl";
import type InputType from "../../../Input/InputType";
import { JsonInputType } from "../../../Input/JsonInputType";
import Oval from "../Oval";

export default class Danish extends Oval {
	public static code = "plate_oval_danish";

	public getDimensionsString(input: InputType, intl: IntlShape) {
		const { length_1, length_2 } = this.formatInputs(input, intl, "decimal");
		const { width_1, width_2 } = this.formatInputs(input, intl);
		if (length_1 && width_1) {
			if (length_2 && width_2) {
				return intl.formatMessage(
					{
						defaultMessage: "Buiten {length_1} x {width_1}, binnen {length_2} x {width_2}",
						id: "QMkatN",
					},
					{ length_1, width_1, length_2, width_2 },
				);
			}

			return intl.formatMessage(
				{
					defaultMessage: "Buiten {length_1} x {width_1}",
					id: "fxZ3Mb",
				},
				{ length_1, width_1 },
			);
		}

		return undefined;
	}

	public calculateSheetDimensions(input: InputType): { length: number; width: number } | undefined {
		const length = Number(input[JsonInputType.LengthSection1]);
		const width = Number(input[JsonInputType.WidthSection1]);

		if (!(Number.isNaN(length) || Number.isNaN(width))) {
			return { length, width };
		}
	}

	public static calculateArc(
		length: number,
		circleHeight: number,
		circleRadius = 0,
		angle = 0,
		arcLength = 0,
	) {
		const arc = { circleRadius, angle, arcLength };

		if (circleHeight > 0) {
			arc.circleRadius = (length ** 2 / 4 + circleHeight ** 2) / (circleHeight * 2);
			arc.angle = 2 * Math.acos(1 - circleHeight / circleRadius);
			arc.arcLength = circleRadius * angle;
		}

		return { circleRadius: arc.circleRadius, angle: arc.angle, arcLength: arc.arcLength };
	}

	protected calculateOutlineLength(
		length: number,
		width: number,
		innerLength?: number,
		innerWidth?: number,
	): number {
		const squareFactor = 1.05;

		const calculatedInnerLength =
			innerLength ??
			Math.max(
				length - (width - Math.sqrt((width / 2) ** 2 * 2)) * squareFactor,
				Math.sqrt((length / 2) ** 2 * 2) * squareFactor,
			);
		const calculatedInnerWidth =
			innerWidth ??
			Math.max(
				width - (length - Math.sqrt((length / 2) ** 2 * 2)) * squareFactor,
				Math.sqrt((width / 2) ** 2 * 2) * squareFactor,
			);

		const [{ arcLength: arcLengthwise }, { arcLength: arcWidthWise }] = [
			Danish.calculateArc(calculatedInnerLength, (length - calculatedInnerLength) / 2),
			Danish.calculateArc(calculatedInnerWidth, (width - calculatedInnerWidth) / 2),
		];

		return arcLengthwise * 2 + arcWidthWise * 2;
	}
}
